import { useState } from 'react'
import { Card, Form, Row, Col, Button, Badge, Alert } from 'react-bootstrap'
import { useAppStore } from '../stores/appStore'

const GroupSelector = ({ 
  onTaskSelectionChange, 
  onAccountSelectionChange,
  onConfigChange,
  taskManager,
  runtimeData,
  updateConfigOverride
}) => {
  const { 
    getRuntimeData,
    updateGlobalTaskConfig
  } = useAppStore()
  const currentRuntimeData = runtimeData || getRuntimeData()
  const effectiveTaskGroups = currentRuntimeData.taskGroups || {}
  const effectiveUserGroups = currentRuntimeData.userGroups || {}
  const effectiveAccounts = currentRuntimeData.accounts || {}
  
  const [selectedTaskGroup, setSelectedTaskGroup] = useState('')
  const [selectedUserGroup, setSelectedUserGroup] = useState('')

  const handleTaskGroupSelect = (groupId) => {
    if (!groupId) {
      setSelectedTaskGroup('')
      return
    }

    const group = effectiveTaskGroups[groupId]
    if (!group) return

    setSelectedTaskGroup(groupId)
    
    // 应用任务组的任务选择
    onTaskSelectionChange(group.selectedTasks)
    
    // 应用任务组的配置到全局配置
    Object.entries(group.taskConfigs || {}).forEach(([taskId, config]) => {
      if (updateConfigOverride) {
        updateConfigOverride(taskId, config)
      } else {
        updateGlobalTaskConfig(taskId, config)
      }
    })
    
    // 同时更新本地配置状态
    onConfigChange(group.taskConfigs || {})
  }

  const handleUserGroupSelect = (groupId) => {
    if (!groupId) {
      setSelectedUserGroup('')
      return
    }

    const group = effectiveUserGroups[groupId]
    if (!group) return

    setSelectedUserGroup(groupId)
    
    // 应用用户组的账号选择
    onAccountSelectionChange(group.selectedAccounts)
  }

  const getCategoryColor = (category) => {
    const colorMap = {
      'resource': 'success',
      'character': 'primary',
      'battle': 'danger',
      'reward': 'warning',
      'activity': 'info',
      'general': 'secondary',
      'example': 'dark'
    }
    return colorMap[category] || 'secondary'
  }

  const getServerColor = (server) => {
    const colors = ['primary', 'success', 'info', 'warning', 'danger', 'secondary']
    const hash = String(server || '').split('').reduce((sum, char) => sum + char.charCodeAt(0), 0)
    return colors[hash % colors.length]
  }

  return (
    <Card className="mb-3">
      <Card.Header>
        <h6 className="mb-0">快速选择组合</h6>
      </Card.Header>
      <Card.Body>
        <Row className="g-3">
          {/* 任务组选择 */}
          <Col md={6}>
            <Form.Group>
              <Form.Label>选择任务组</Form.Label>
              <Form.Select
                value={selectedTaskGroup}
                onChange={(e) => handleTaskGroupSelect(e.target.value)}
              >
                <option value="">-- 选择任务组 --</option>
                {Object.entries(effectiveTaskGroups).map(([groupId, group]) => (
                  <option key={groupId} value={groupId}>
                    {group.name} ({group.selectedTasks.length}个任务)
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {/* 显示选中的任务组信息 */}
            {selectedTaskGroup && effectiveTaskGroups[selectedTaskGroup] && (
              <div className="mt-2 p-2 bg-light rounded">
                <div className="small mb-1">
                  <strong>{effectiveTaskGroups[selectedTaskGroup].name}</strong>
                </div>
                <div className="small text-muted mb-2">
                  {effectiveTaskGroups[selectedTaskGroup].description}
                </div>
                <div className="d-flex flex-wrap gap-1">
                  {effectiveTaskGroups[selectedTaskGroup].selectedTasks.slice(0, 5).map(taskId => {
                    const task = taskManager?.getTask(taskId)
                    if (!task) return null
                    return (
                      <Badge 
                        key={taskId} 
                        bg={getCategoryColor(task.category)}
                        className="small"
                      >
                        {task.icon} {task.name}
                      </Badge>
                    )
                  })}
                  {effectiveTaskGroups[selectedTaskGroup].selectedTasks.length > 5 && (
                    <Badge bg="secondary" className="small">
                      +{effectiveTaskGroups[selectedTaskGroup].selectedTasks.length - 5}
                    </Badge>
                  )}
                </div>
              </div>
            )}

            {Object.keys(effectiveTaskGroups).length === 0 && (
              <Alert variant="info" className="mt-2 mb-0">
                暂无任务组，请先创建任务组
              </Alert>
            )}
          </Col>

          {/* 用户组选择 */}
          <Col md={6}>
            <Form.Group>
              <Form.Label>选择用户组</Form.Label>
              <Form.Select
                value={selectedUserGroup}
                onChange={(e) => handleUserGroupSelect(e.target.value)}
              >
                <option value="">-- 选择用户组 --</option>
                {Object.entries(effectiveUserGroups).map(([groupId, group]) => (
                  <option key={groupId} value={groupId}>
                    {group.name} ({group.selectedAccounts.length}个账号)
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {/* 显示选中的用户组信息 */}
            {selectedUserGroup && effectiveUserGroups[selectedUserGroup] && (
              <div className="mt-2 p-2 bg-light rounded">
                <div className="small mb-1">
                  <strong>{effectiveUserGroups[selectedUserGroup].name}</strong>
                </div>
                <div className="small text-muted mb-2">
                  {effectiveUserGroups[selectedUserGroup].description}
                </div>
                <div className="d-flex flex-wrap gap-1">
                  {effectiveUserGroups[selectedUserGroup].selectedAccounts.slice(0, 5).map(accountKey => {
                    const account = effectiveAccounts[accountKey]
                    if (!account) return null
                    return (
                      <Badge 
                        key={accountKey} 
                        bg={getServerColor(account.server)}
                        className="small"
                      >
                        {account.username} {account.server}
                      </Badge>
                    )
                  })}
                  {effectiveUserGroups[selectedUserGroup].selectedAccounts.length > 5 && (
                    <Badge bg="secondary" className="small">
                      +{effectiveUserGroups[selectedUserGroup].selectedAccounts.length - 5}
                    </Badge>
                  )}
                </div>
              </div>
            )}

            {Object.keys(effectiveUserGroups).length === 0 && (
              <Alert variant="info" className="mt-2 mb-0">
                暂无用户组，请先创建用户组
              </Alert>
            )}
          </Col>
        </Row>

        {/* 清除选择按钮 */}
        {(selectedTaskGroup || selectedUserGroup) && (
          <div className="mt-3 pt-2 border-top">
            <div className="d-flex gap-2">
              {selectedTaskGroup && (
                <Button 
                  variant="outline-secondary" 
                  size="sm"
                  onClick={() => handleTaskGroupSelect('')}
                >
                  清除任务组选择
                </Button>
              )}
              {selectedUserGroup && (
                <Button 
                  variant="outline-secondary" 
                  size="sm"
                  onClick={() => handleUserGroupSelect('')}
                >
                  清除用户组选择
                </Button>
              )}
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  )
}

export default GroupSelector
